/* customer-segmentation.js
 *
 */

(function customerSegmentation() {
	const STORAGE_KEY = 'userState';
	const SEGMENT_NEW = 'newCustomer';
	const SEGMENT_OLD = 'returningCustomer';
	const SEGMENT_UNKNOWN = 'unknown';
	const ONE_DAY_MS = 24 * 60 * 60 * 1000;
	const SIGNIFICANT_TOUCH_KEYS = [
		'utm_source',
		'utm_medium',
		'utm_campaign',
		'utm_term',
		'utm_content',
		'gclid',
		'fbclid',
		'ttclid',
		'msclkid',
	];

	// --- Utilities -------------------------------------------------------------
	function safeJsonParse(jsonString) {
		try {
			return JSON.parse(jsonString);
		} catch (error) {
			return null;
		}
	}

	function safeJsonStringify(value) {
		try {
			return JSON.stringify(value);
		} catch (error) {
			return null;
		}
	}

	function readFromLocalStorage(key) {
		return window.localStorage.getItem(key);
	}

	function writeToLocalStorage(key, value) {
		window.localStorage.setItem(key, value);
		return true;
	}
	// TODO: REFACTOR
	async function setUserStatusAttribute(segment) {
		if (!segment) return;
		try {
			const res = await fetch(`${Shopify.routes.root}cart/update.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					attributes: {
						customerSegment: segment,
					},
				}),
			});
			const resData = await res.json();
		} catch (e) {
			console.log('Failed to update cart attributes:', e);
		}
	}
	function dispatchSegmentUpdatedEvent(state) {
		try {
			setTimeout(async () => {
				await setUserStatusAttribute(state.segment);
			}, 1000);
		} catch (error) {
			// No-op: CustomEvent can be blocked in very old browsers or unusual environments.
		}
	}

	// --- Data model ------------------------------------------------------------

	function createEmptyState() {
		return {
			segment: SEGMENT_NEW,
			purchaseCount: 0,
			flags: {
				likelyFromAds: false, // come from ads
				returningVisitor: false, // more than one session
				isOldVisitor: false, // first session was older then 1 day
				hasAtLeastOnePurchase: false, // at least one purchase made
				hasNoABlyftInfo: false, // no ablyft_uvs found in localStorage
				klaviyoChatarminOldCustomer: false, // klaviyo old customer flag
			},
		};
	}

	// remove unexpected fields, ensure correct types
	function normalizeState(inputCustomerState) {
		const normalizedState = createEmptyState();
		if (inputCustomerState && typeof inputCustomerState === 'object') {
			// Copy known fields only (defensive against unexpected schema changes).
			normalizedState.segment =
				inputCustomerState.segment === SEGMENT_OLD
					? SEGMENT_OLD
					: inputCustomerState.segment === SEGMENT_NEW
						? SEGMENT_NEW
						: SEGMENT_UNKNOWN;
			normalizedState.purchaseCount = Number(inputCustomerState.purchaseCount || 0) || 0;
			normalizedState.flags = normalizedState.flags || {};
			const flags = inputCustomerState.flags || {};
			normalizedState.flags.likelyFromAds = Boolean(flags.likelyFromAds);
			normalizedState.flags.returningVisitor = Boolean(flags.returningVisitor);
			normalizedState.flags.isOldVisitor = Boolean(flags.isOldVisitor);
			normalizedState.flags.hasAtLeastOnePurchase = Boolean(flags.hasAtLeastOnePurchase);
			normalizedState.flags.hasNoABlyftInfo = Boolean(flags.hasNoABlyftInfo);
			normalizedState.flags.klaviyoChatarminOldCustomer = Boolean(flags.klaviyoChatarminOldCustomer);
		}
		return normalizedState;
	}
	function loadOrCreateState() {
		const raw = readFromLocalStorage(STORAGE_KEY);
		const parsed = raw ? safeJsonParse(raw) : null;
		return normalizeState(parsed);
	}

	function saveState(state) {
		const json = safeJsonStringify(state);
		if (!json) return false;
		return writeToLocalStorage(STORAGE_KEY, json);
	}

	// --- Attribution and weak signals -----------------------------------------

	function readQueryParam(urlSearchParams, key) {
		const value = urlSearchParams.get(key);
		return value ? String(value) : null;
	}

	function readKlaviyoChatarminSignal() {
		try {
			const url = new URL(window.location.href);
			return url.searchParams.get('customerSegment') === 'oldCustomer';
		} catch {
			return false;
		}
	}

	function collectTouchData() {
		const url = new URL(window.location.href);
		const params = url.searchParams;
		const touchData = {};
		SIGNIFICANT_TOUCH_KEYS.forEach((current_key) => {
			const value = readQueryParam(params, current_key);
			if (value) {
				touchData[current_key] = value;
			}
		});
		return touchData;
	}

	function isLikelyFromAds(touchData) {
		return SIGNIFICANT_TOUCH_KEYS.some((key) => key in touchData && touchData[key] !== null);
	}

	function tryReadAblyftSessions() {
		// ABlyft commonly persists visitor/session info in localStorage (example key: "ablyft_uvs").
		// This is only a weak “returning visitor” signal; it must NOT classify someone as "old customer".
		const raw = readFromLocalStorage('ablyft_uvs');
		if (!raw) return null;

		const parsed = safeJsonParse(raw);
		if (!parsed || typeof parsed !== 'object') return null;
		return parsed;
	}

	// --- Classification rules (conservative) -----------------------------------

	function applyConservativeClassification(customerState) {
		if (customerState.flags.hasAtLeastOnePurchase) {
			customerState.segment = SEGMENT_OLD;
			return;
		}
		if (customerState.flags.klaviyoChatarminOldCustomer) {
			customerState.segment = SEGMENT_OLD;
			return;
		}
		if (customerState.flags.isOldVisitor || customerState.flags.returningVisitor) {
			customerState.segment = SEGMENT_OLD;
			return;
		}
		if (customerState.flags.likelyFromAds) {
			customerState.segment = SEGMENT_NEW;
			return;
		}
		if (customerState.flags.hasNoABlyftInfo) {
			customerState.segment = SEGMENT_NEW;
			return;
		}
		customerState.segment = SEGMENT_UNKNOWN;
	}
	function initialize() {
		// LOAD EXISTING STATE or CREATE DEFAULT
		const customerState = loadOrCreateState();

		const touchData = collectTouchData();
		const likelyFromAds = isLikelyFromAds(touchData);

		const hasAtLeastOnePurchase = customerState.purchaseCount > 0;

		const ablyftData = tryReadAblyftSessions();
		const returningVisitor = Boolean(ablyftData && typeof ablyftData.sessions === 'number' && ablyftData.sessions > 1);
		const firstSessionTimestampMs = ablyftData && ablyftData.first ? ablyftData.first * 1000 : null;
		const isOldVisitor = firstSessionTimestampMs ? Date.now() - firstSessionTimestampMs > ONE_DAY_MS : false;
		const klaviyoChatarminOldCustomer = readKlaviyoChatarminSignal();

		customerState.flags.likelyFromAds = likelyFromAds;
		customerState.flags.returningVisitor = returningVisitor;
		customerState.flags.isOldVisitor = isOldVisitor;
		customerState.flags.hasAtLeastOnePurchase = hasAtLeastOnePurchase;
		customerState.flags.hasNoABlyftInfo = customerState.flags.hasNoABlyftInfo || !ablyftData;
		customerState.flags.klaviyoChatarminOldCustomer =
			customerState.flags.klaviyoChatarminOldCustomer || klaviyoChatarminOldCustomer;

		applyConservativeClassification(customerState);

		saveState(customerState);
		dispatchSegmentUpdatedEvent(customerState);
	}
	document.addEventListener('DOMContentLoaded', initialize());
})();
